import {
  PrimitiveFn,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import { Recommendation } from "@mirage/shared/search/recommendation";
import * as sourceScore from "../scoring/source-score";
import { twoStageSearch } from "../search/two-stage-search";

export function onlySummarizableRecommendations(
  candidates: Recommendation[],
  onlyDocuments: boolean,
): Recommendation[] {
  return candidates.filter(
    (candidate) =>
      candidate.summarizable === "yes_summarizable" &&
      (onlyDocuments ? candidate.recordType[".tag"] === "document" : true),
  );
}

export function removeContactsRecommendations(
  candidates: Recommendation[],
): Recommendation[] {
  return candidates.filter(
    (candidate) => candidate.recordType?.[".tag"] !== "contact",
  );
}

export function typingStateRecommendations<T extends Recommendation>(
  query: string,
  recommendations: T[],
  sourceLimit: number,
) {
  // box recommendations into a SearchableResults array
  const searchableResultCandidates = recommendations.map((recommendation) => ({
    searchableStrings: [recommendation.title],
    originalResult: recommendation,
  }));

  // and let twoStageSearch handle the actual searching!
  const matchingSearchableResults = twoStageSearch<T>(
    query,
    searchableResultCandidates,
    {
      limit: sourceLimit,
      isRecents: false,
    },
  );

  // and then we just need to extract the original results from the SearchableResults
  return matchingSearchableResults.map(
    (searchableResult) => searchableResult.originalResult,
  );
}

export function nullStateRecommendations<T extends Recommendation>(
  recommendations: T[],
  sourceLimit: number,
): T[] {
  return sortLastClickedMsDesc(recommendations, sourceLimit);
}

export function sortLastClickedMsDesc<T extends Recommendation>(
  recommendations: T[],
  sourceLimit: number,
): T[] {
  const sorted = recommendations.sort(
    (a: T, b: T) => (b.lastClickedMs || 0) - (a.lastClickedMs || 0),
  );
  return sorted.slice(0, sourceLimit);
}

export function filterRecommendations(
  recommendations: Recommendation[],
  query: string,
  _mode: typeahead.Mode,
  { config }: typeahead.TaggedSearchOptions,
  {
    onlyDocuments = false,
    allowContacts = false,
    filterResultLimit,
  }: {
    onlyDocuments?: boolean;
    allowContacts?: boolean;
    filterResultLimit: number;
  },
): Recommendation[] {
  let results = recommendations;

  if (!allowContacts) {
    results = removeContactsRecommendations(results);
  }

  // Filter for summarizable results if the flag is enabled
  if (config.isSummarizableRecommendationsOnly || onlyDocuments) {
    results = onlySummarizableRecommendations(results, onlyDocuments);
  }

  if (query === "") {
    results = nullStateRecommendations(results, filterResultLimit);
  } else {
    results = typingStateRecommendations(query, results, filterResultLimit);
  }

  return results;
}

export function tagAndInjectHitsFactory<
  T extends
    | typeahead.ResultType.DraftWithSource
    | typeahead.ResultType.Recommendation,
>(
  primitive: PrimitiveFn<T>,
  getUuid: (result: typeahead.PrimitiveMap[T]) => string,
  sourceLimit: number,
  sectionId: typeahead.SectionId | undefined,
) {
  return (
    results: typeahead.PrimitiveMap[T][],
    query: string,
    _mode: typeahead.Mode,
    { config, cache }: typeahead.TaggedSearchOptions,
  ) => {
    return results
      .map((result) => {
        // we can have click tracking from the server through recommendations,
        // and we can have click tracking locally. The server can be delayed,
        // so in order to provide the best user experience, we use whichever
        // source has the highest non-title-match score for the recommendation
        // that way when the server catches up, it'll just start transparently
        // using the server-side score over the client-side score
        const resultWithLocalHits = primitive(getUuid(result), result, {
          hits: cache.getHits(result.uuid),
          sectionId,
        });
        const resultWithRemoteHits = primitive(getUuid(result), result, {
          hits: {
            history: result.clicks,
            mostRecentMs: result.lastClickedMs,
          },
          sectionId,
        });

        // NOTE (DBG, 08/06/25): This type coercion is necessary but does not mutate any rules.
        // Because of the way TaggedResult was originally typed, even though at runtime
        // the resultWithLocalHits and resultWithRemoteHits are of type TaggedResult,
        // we need to coerce them to TaggedResult to satisfy the type checker.
        // This could be fixed by re-typing TaggedResult to be more permissive,
        // but that would be a larger refactor with other dependencies.
        const localScoredResult = sourceScore.score(
          query,
          config,
        )(resultWithLocalHits as typeahead.TaggedResult);
        const remoteScoredResult = sourceScore.score(
          query,
          config,
        )(resultWithRemoteHits as typeahead.TaggedResult);

        return localScoredResult.score > remoteScoredResult.score
          ? localScoredResult
          : remoteScoredResult;
      })
      .sort((a, b) => b.score! - a.score!)
      .slice(0, sourceLimit);
  };
}
